import { trackUserInteraction } from 'actions/tracking';
import * as t from 'data';
import { useDispatch, useHistory, useSelector } from 'hooks';
import { Heading, Text, Button, Row } from 'mymoria-ui/components';
import { styled, css, mq } from 'mymoria-ui/utils';
import React, { FunctionComponent } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { isOfferEditable } from 'reducers/proposal';
import { ProductCategory, Product, Service } from 'types';
import FavoriteProductsItem from './FavoriteProductsItem';

interface FavoriteProductsCardProps {
  category?: ProductCategory;
  favoriteProductsIds: string[];
  selectedItemsIds: string[];
  selectedItem?: Product | Service;
}

const CatalogProductsCard = styled.div(
  ({ theme: { space } }) => css`
    padding: 0 ${space[4]};

    img {
      width: 100%;
      height: 140px;
      border-radius: 18px;
      object-fit: cover;
    }

    .row {
      gap: ${space[6]};
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      grid-auto-rows: 1fr;
      grid-auto-flow: dense;
      overflow: hidden;

      ${mq({
        gridTemplateColumns: [
          'repeat(auto-fill, minmax(120px, 1fr))',
          'repeat(auto-fill, minmax(160px, 1fr))',
        ],
        height: ['215px', '200px'],
      })}
    }

    .col {
      cursor: pointer;
      box-sizing: border-box;
    }
  `,
);

const FavoriteProductsCard: FunctionComponent<FavoriteProductsCardProps> = ({
  category,
  favoriteProductsIds,
  selectedItemsIds,
  selectedItem,
}) => {
  const dispatch = useDispatch();
  const { push } = useHistory();
  const { url } = useRouteMatch();
  const isEditable = useSelector(isOfferEditable());

  const cataloguesCardTitleShort = category
    ? t.global.cataloguesCard.titleByCategory[category].short
    : undefined;

  const cataloguesCardTitleLong = category
    ? t.global.cataloguesCard.titleByCategory[category].long
    : undefined;

  const cataloguesEditButton = category ? t.global.cataloguesCard.editButton[category] : undefined;

  const handleEdit = () => {
    dispatch(trackUserInteraction(`Edit Product`, `${category} catalog card`));
    push(`${url}/products/${category}`);
  };

  // hide whole section when there is only 1 favorite item that is selected at the same time.
  const shouldRender = !(
    favoriteProductsIds.length === 1 &&
    !!selectedItem &&
    favoriteProductsIds.includes(selectedItem.id)
  );

  return shouldRender ? (
    <CatalogProductsCard>
      {!!selectedItem ? (
        <Heading.Heading3 pb="large">{cataloguesCardTitleShort}</Heading.Heading3>
      ) : (
        <Text pb="large">{cataloguesCardTitleLong}</Text>
      )}
      <Row gap="12rem" className="row" flexWrap="nowrap" overflow="hidden">
        {favoriteProductsIds?.map(
          sku => !selectedItemsIds.includes(sku) && <FavoriteProductsItem key={sku} sku={sku} />,
        )}
      </Row>
      <Button block variant="secondary" my="normal" onClick={isEditable ? handleEdit : undefined}>
        {cataloguesEditButton}
      </Button>
    </CatalogProductsCard>
  ) : null;
};

export default FavoriteProductsCard;
